export interface NormalizeArrayOptions<T> {
  readonly allowSingleObject?: boolean | undefined;
  readonly itemName?: string | undefined;
  readonly validateItem?: ((value: unknown) => value is T) | undefined;
}

type RecordLike = Record<string, unknown>;

const ARRAY_WRAPPER_KEYS = ["items", "data", "results", "result", "value", "records", "rows", "content"] as const;
const NESTED_WRAPPER_KEYS = ["data", "page", "payload", "result"] as const;
const ERROR_KEYS = ["errors", "error", "errorCode", "traceId"] as const;

export function normalizeApiArrayResponse<T>(input: unknown, options: NormalizeArrayOptions<T> = {}): T[] {
  if (input === null || input === undefined) return [];
  if (isApiErrorEnvelope(input)) return [];
  const arrayValue = extractArray(input, 0);
  const rows = arrayValue ?? (options.allowSingleObject && isRecord(input) ? [input] : []);
  if (!options.validateItem) return rows as T[];
  return rows.filter(options.validateItem);
}

export function isApiErrorEnvelope(input: unknown): boolean {
  if (!isRecord(input)) return false;
  const success = input.success;
  if (success === false) return true;
  if (typeof input.type === "string" && typeof input.title === "string" && typeof input.status === "number") return true;
  if (typeof input.errorCode === "string" || typeof input.code === "string" && typeof input.message === "string" && input.data === undefined && input.items === undefined) return true;
  if (input.error instanceof Error || typeof input.error === "string") return true;
  if (Array.isArray(input.errors) && input.data === undefined && input.items === undefined && input.results === undefined) return true;
  return ERROR_KEYS.some((key) => key in input) && input.data === undefined && input.items === undefined && input.results === undefined && input.records === undefined;
}

function extractArray(input: unknown, depth: number): unknown[] | undefined {
  if (Array.isArray(input)) return input;
  if (!isRecord(input) || depth > 4 || isApiErrorEnvelope(input)) return undefined;

  for (const key of ARRAY_WRAPPER_KEYS) {
    const value = input[key];
    if (Array.isArray(value)) return value;
  }

  for (const key of NESTED_WRAPPER_KEYS) {
    const value = input[key];
    if (isRecord(value)) {
      const nested = extractArray(value, depth + 1);
      if (nested) return nested;
    }
  }

  return undefined;
}

function isRecord(value: unknown): value is RecordLike {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
